import {TextField, InputAdornment} from '@mui/material'
import {StyledTitle, StyledSearchTextField} from '../../AdminPanelStyling'
import {NavigationBox, NavigationItem} from '../../ProjectDetails/ProjectDetailStyle'
import {useState, useEffect, useMemo} from 'react'
import {MainBoxClient} from '../../CreateClient/CreateClientStyles'
import PaginatedLogTable from './PaginatedLogTable'
import SearchIcon from '@mui/icons-material/Search'
import {useAuditDetails} from '../../api/getAuditDetails'
import {useUserAuditDetails} from '../../api/getUserAuditDetails'
import {useClientAuditDetails} from '../../api/getClientAuditDetails'
import {transformAuditDataToTableFormat, PROJECT_AUDIT_COLUMNS} from './helpers/auditDataTransformer'
import {transformUserAuditDataToTableFormat, USER_AUDIT_COLUMNS} from './helpers/userAuditDataTransformer'
import {transformClientAuditDataToTableFormat, CLIENT_AUDIT_COLUMNS} from './helpers/clientAuditDataTransformer'
import LoadingIcon from '../../common/LoadingIcon/LoadingIcon'
import {useRolePermissions} from '../../../hooks/useRolesPermissions'

const DEFAULT_PAGE_SIZE = 5

const ViewChangeLogs = () => {
  const Tabs = {
    CLIENTS: 'clients',
    PROJECTS: 'projects',
    USERS: 'users'
  }

  const {isSuperAdmin, isDeliveryAdmin, isCustomerSupport} = useRolePermissions()

  const allowedTabs = useMemo(() => {
    if (isSuperAdmin() || isCustomerSupport()) return [Tabs.CLIENTS, Tabs.PROJECTS, Tabs.USERS]
    if (isDeliveryAdmin()) return [Tabs.PROJECTS, Tabs.USERS]
    return [Tabs.CLIENTS, Tabs.PROJECTS, Tabs.USERS]
  }, [isSuperAdmin, isDeliveryAdmin, isCustomerSupport, Tabs.CLIENTS, Tabs.PROJECTS, Tabs.USERS])

  const [activeTab, setActiveTab] = useState(allowedTabs[0])
  const [data, setData] = useState({
    columns: allowedTabs[0] === Tabs.CLIENTS ? CLIENT_AUDIT_COLUMNS : PROJECT_AUDIT_COLUMNS,
    rows: [],
    pagination: {page: 1, pageSize: DEFAULT_PAGE_SIZE, totalCount: 0}
  })
  const [clientPage, setClientPage] = useState(1)
  const [clientPageSize, setClientPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [projectPage, setProjectPage] = useState(1)
  const [projectPageSize, setProjectPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [userPage, setUserPage] = useState(1)
  const [userPageSize, setUserPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [searchTerm, setSearchTerm] = useState('')

  // Keep activeTab synchronized with allowedTabs
  useEffect(() => {
    if (!allowedTabs.includes(activeTab)) {
      setActiveTab(allowedTabs[0])
    }
  }, [allowedTabs, activeTab])

  const {clientAuditDetailsData, loadingClientAuditDetails, errorClientAuditDetails} = useClientAuditDetails({
    page: clientPage,
    pageSize: clientPageSize,
    skip: activeTab !== Tabs.CLIENTS || !allowedTabs.includes(Tabs.CLIENTS)
  })

  const {auditDetailsData, loadingAuditDetails, errorAuditDetails} = useAuditDetails({
    fetchType: 'project',
    page: projectPage,
    pageSize: projectPageSize,
    skip: activeTab !== Tabs.PROJECTS || !allowedTabs.includes(Tabs.PROJECTS)
  })

  const {userAuditDetailsData, loadingUserAuditDetails, errorUserAuditDetails} = useUserAuditDetails({
    page: userPage,
    pageSize: userPageSize,
    skip: activeTab !== Tabs.USERS || !allowedTabs.includes(Tabs.USERS)
  })

  const handleTabClick = (tab) => {
    setActiveTab(tab)
    setSearchTerm('')
    if (tab === Tabs.CLIENTS) {
      setClientPage(1)
      setClientPageSize(DEFAULT_PAGE_SIZE)
    }
    if (tab === Tabs.PROJECTS) {
      setProjectPage(1)
      setProjectPageSize(DEFAULT_PAGE_SIZE)
    }
    if (tab === Tabs.USERS) {
      setUserPage(1)
      setUserPageSize(DEFAULT_PAGE_SIZE)
    }
  }

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
  }

  const handleClientPageChange = (newPage) => {
    setClientPage(newPage + 1)
    setSearchTerm('')
  }

  const handleClientPageSizeChange = (newPageSize) => {
    setClientPageSize(newPageSize)
    setClientPage(1)
    setSearchTerm('')
  }

  const handleProjectPageChange = (newPage) => {
    setProjectPage(newPage + 1)
    setSearchTerm('')
  }

  const handleProjectPageSizeChange = (newPageSize) => {
    setProjectPageSize(newPageSize)
    setProjectPage(1)
    setSearchTerm('')
  }

  const handleUserPageChange = (newPage) => {
    setUserPage(newPage + 1)
    setSearchTerm('')
  }

  const handleUserPageSizeChange = (newPageSize) => {
    setUserPageSize(newPageSize)
    setUserPage(1)
    setSearchTerm('')
  }
  useEffect(() => {
    switch (activeTab) {
      case Tabs.CLIENTS:
        if (clientAuditDetailsData) {
          setData(transformClientAuditDataToTableFormat(clientAuditDetailsData))
        } else {
          setData({
            columns: CLIENT_AUDIT_COLUMNS,
            rows: [],
            pagination: {page: 1, pageSize: DEFAULT_PAGE_SIZE, totalCount: 0}
          })
        }
        break
      case Tabs.PROJECTS:
        if (auditDetailsData) {
          setData(transformAuditDataToTableFormat(auditDetailsData))
        } else {
          setData({
            columns: PROJECT_AUDIT_COLUMNS,
            rows: [],
            pagination: {page: 1, pageSize: DEFAULT_PAGE_SIZE, totalCount: 0}
          })
        }
        break
      case Tabs.USERS:
        if (userAuditDetailsData) {
          setData(transformUserAuditDataToTableFormat(userAuditDetailsData))
        } else {
          setData({
            columns: USER_AUDIT_COLUMNS,
            rows: [],
            pagination: {page: 1, pageSize: DEFAULT_PAGE_SIZE, totalCount: 0}
          })
        }
        break
      default:
        break
    }
  }, [
    activeTab,
    clientAuditDetailsData,
    auditDetailsData,
    userAuditDetailsData,
    Tabs.CLIENTS,
    Tabs.PROJECTS,
    Tabs.USERS
  ])

  const renderClientsTable = () => {
    if (loadingClientAuditDetails) {
      return <LoadingIcon title='Loading client audit logs' />
    }

    if (errorClientAuditDetails) {
      return <LoadingIcon title='Error loading client audit logs. Please try again.' />
    }

    return (
      <PaginatedLogTable
        data={data}
        sortedBy='timestamp'
        serverSidePagination={true}
        totalCount={data.pagination?.totalCount || 0}
        currentPage={clientPage - 1}
        currentPageSize={clientPageSize}
        onPageChange={handleClientPageChange}
        onPageSizeChange={handleClientPageSizeChange}
        searchTerm={searchTerm}
        renderLongTableCellFor={['afterChange']}
      />
    )
  }

  const renderProjectsTable = () => {
    if (loadingAuditDetails) {
      return <LoadingIcon title='Loading project audit logs' />
    }

    if (errorAuditDetails) {
      return <LoadingIcon title='Error loading project audit logs. Please try again.' />
    }

    return (
      <PaginatedLogTable
        data={data}
        sortedBy='timestamp'
        serverSidePagination={true}
        totalCount={data.pagination?.totalCount || 0}
        currentPage={projectPage - 1}
        currentPageSize={projectPageSize}
        onPageChange={handleProjectPageChange}
        onPageSizeChange={handleProjectPageSizeChange}
        searchTerm={searchTerm}
        renderLongTableCellFor={['clientName', 'projectName', 'values']}
      />
    )
  }

  const renderUsersTable = () => {
    if (loadingUserAuditDetails) {
      return <LoadingIcon title='Loading user audit logs' />
    }

    if (errorUserAuditDetails) {
      return <LoadingIcon title='Error loading user audit logs. Please try again.' />
    }

    return (
      <PaginatedLogTable
        data={data}
        sortedBy='timestamp'
        serverSidePagination={true}
        totalCount={data.pagination?.totalCount || 0}
        currentPage={userPage - 1}
        currentPageSize={userPageSize}
        onPageChange={handleUserPageChange}
        onPageSizeChange={handleUserPageSizeChange}
        searchTerm={searchTerm}
        renderLongTableCellFor={['clientName', 'projectName']}
      />
    )
  }

  const renderTable = () => {
    if (activeTab === Tabs.CLIENTS) {
      return renderClientsTable()
    }
    if (activeTab === Tabs.PROJECTS) {
      return renderProjectsTable()
    }
    if (activeTab === Tabs.USERS) {
      return renderUsersTable()
    }
    return <PaginatedLogTable data={data} sortedBy='id' searchTerm={searchTerm} />
  }

  return (
    <MainBoxClient component='main'>
      <>
        <StyledTitle variant='h6' tabIndex={0}>
          View Change Logs
        </StyledTitle>

        <NavigationBox>
          {allowedTabs.includes(Tabs.CLIENTS) && (
            <NavigationItem selected={activeTab === Tabs.CLIENTS} onClick={() => handleTabClick(Tabs.CLIENTS)}>
              Clients
            </NavigationItem>
          )}
          {allowedTabs.includes(Tabs.PROJECTS) && (
            <NavigationItem selected={activeTab === Tabs.PROJECTS} onClick={() => handleTabClick(Tabs.PROJECTS)}>
              Projects
            </NavigationItem>
          )}
          {allowedTabs.includes(Tabs.USERS) && (
            <NavigationItem selected={activeTab === Tabs.USERS} onClick={() => handleTabClick(Tabs.USERS)}>
              Users
            </NavigationItem>
          )}
        </NavigationBox>

        <StyledSearchTextField
          component={TextField}
          id='outlined-search'
          type='search'
          placeholder='Search Records'
          variant='outlined'
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            p: 3,
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{fontSize: 15}} />
              </InputAdornment>
            )
          }}
        />

        {renderTable()}
      </>
    </MainBoxClient>
  )
}

export default ViewChangeLogs
